// Magic-byte / content sniffing for the file uploads in BinaryDetails and
// ConfigWorkSpaceDetails. All checks are client-side UX guards — the server
// remains the source of truth.

async function readHead(file, n = 64) {
  const slice = file.slice(0, Math.min(n, file.size));
  const buf = await slice.arrayBuffer();
  return new Uint8Array(buf);
}

const startsWith = (bytes, sig) => sig.every((b, i) => bytes[i] === b);

const EXEC_SIGS = {
  PE: [0x4D, 0x5A], // 'MZ' Windows PE/COFF
  ELF: [0x7F, 0x45, 0x4C, 0x46], // Linux/Unix ELF
  MACHO_LE_32: [0xCE, 0xFA, 0xED, 0xFE],
  MACHO_LE_64: [0xCF, 0xFA, 0xED, 0xFE],
  MACHO_BE_32: [0xFE, 0xED, 0xFA, 0xCE],
  MACHO_BE_64: [0xFE, 0xED, 0xFA, 0xCF],
  MACHO_FAT:   [0xCA, 0xFE, 0xBA, 0xBE],
};

const PRETTY = {
  windows: 'Windows PE (.exe)',
  linux: 'Linux ELF',
  mac: 'macOS Mach-O',
};

export function detectExecutableType(bytes) {
  if (startsWith(bytes, EXEC_SIGS.PE)) return 'windows';
  if (startsWith(bytes, EXEC_SIGS.ELF)) return 'linux';
  if (
    startsWith(bytes, EXEC_SIGS.MACHO_LE_32) ||
    startsWith(bytes, EXEC_SIGS.MACHO_LE_64) ||
    startsWith(bytes, EXEC_SIGS.MACHO_BE_32) ||
    startsWith(bytes, EXEC_SIGS.MACHO_BE_64) ||
    startsWith(bytes, EXEC_SIGS.MACHO_FAT)
  ) return 'mac';
  return null;
}

export async function validateBinarySignature(file, osLabel) {
  if (!file) return { ok: false, detected: null, error: 'No file provided' };
  const head = await readHead(file, 8);
  const detected = detectExecutableType(head);
  if (!detected) {
    return {
      ok: false,
      detected: null,
      error: 'Unrecognised file. Expected a Windows PE (.exe), Linux ELF, or macOS Mach-O binary.',
    };
  }
  const target = (osLabel || '').toLowerCase();
  if (target && detected !== target) {
    return {
      ok: false,
      detected,
      error: `Selected OS is ${osLabel} but the file looks like a ${PRETTY[detected]} binary.`,
    };
  }
  return { ok: true, detected };
}

const CONFIG_EXTS = new Set(['conf', 'yml', 'yaml', 'river']);

// Permissive text-byte check: printable ASCII, common whitespace, or any
// high-bit byte (treated as possible UTF-8). NULs and other control bytes
// flag the file as binary.
function isLikelyTextByte(b) {
  if (b === 0x09 || b === 0x0A || b === 0x0D) return true;
  if (b >= 0x20 && b <= 0x7E) return true;
  if (b >= 0x80) return true;
  return false;
}

export async function validateConfigFile(file) {
  if (!file) return { ok: false, error: 'No file provided' };
  const name = file.name || '';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  if (!CONFIG_EXTS.has(ext)) {
    return { ok: false, error: 'Config must be a .conf, .yml, .yaml, or .toml file.' };
  }
  const head = await readHead(file, 512);
  for (const b of head) {
    if (!isLikelyTextByte(b)) {
      return { ok: false, error: 'File contents look binary, not a text config.' };
    }
  }
  return { ok: true };
}
                                                          